import { renderMetaTags, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import { GetStaticProps } from "next";
import { Api } from "@/api";
import { Layout, SEO } from "@/components";
import { DATOCMS_API_TOKEN } from "@/config";
import {
  MODULE_KEYS,
  Modules,
  ModuleToComponent,
  modulesFragments,
  modulesField,
  metaTagsFragment,
  ResourceFragmentType,
} from "@/modules";
import { Subscription, IJob } from "@/types";

interface ICareersPage {
  jobs: IJob[];
  page: {
    title: string;
    modules: Modules;
    seo: TitleMetaLinkTag[];
  };
}

type Props = {
  preview: boolean;
  subscription: Subscription<ICareersPage>;
};

const CareersPage: React.FC<Props> = ({ preview, subscription }) => {
  const { data } = useQuerySubscription(subscription);
  if (!data) return null;

  const {
    jobs,
    page: { title, modules, seo },
  } = data;

  const _modules = [...modules];

  // Create jobs section
  const items = jobs.map(({ id, title, slug, parentPage }) => ({
    id,
    title,
    internal: true,
    page: { slug, parentPage },
  }));

  const jobsSection: ResourceFragmentType = {
    items,
    _modelApiKey: MODULE_KEYS.resource,
    id: "narrative-current-openings",
    heading: "Current openings",
    headingSize: "h2",
    subheading: !items.length ? <p>Sorry, there are currently no openings available.</p> : null,
    isSection: true,
    footer: (
      <div className="max-w-200 mt-15 mx-auto text-center">
        <p className="mb-4">
          Narrative is an equal opportunity workplace and we strongly believe that diversity is a major strength.
          Creating a diverse workplace is a constant battle in the tech industry but we are working as hard as we can to
          ensure our employment opportunities and workplace is as welcoming to all as possible. Everyone is welcome to
          apply to work at Narrative.
        </p>
        <p>We’re always looking for talented people, so if you don’t see your perfect role drop us an email anyway.</p>
      </div>
    ),
  };

  // Add the jobs section as the second to last module
  _modules.splice(_modules.length - 1, 0, jobsSection);

  return (
    <Layout preview={preview}>
      <SEO title={`${title} | Narrative`}>{renderMetaTags(seo)}</SEO>
      <ModuleToComponent modules={_modules} />
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<Props> = async ({ preview = false }) => {
  const graphqlRequest = {
    preview,
    query: `
      {
        page: pageCareer {
          id
          title
          ${modulesField}
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }

        jobs: allPageJobs {
          id
          title
          slug
          parentPage {
            slug
          }
        }
      }
      ${metaTagsFragment}
      ${modulesFragments}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<ICareersPage>(graphqlRequest);

  return {
    props: {
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default CareersPage;
